import React, { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Result } from 'antd';
import { ChevronDown, ChevronUp, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Dialog, IconButton, Slide, Toolbar, Typography } from '@mui/material';
import LoadingIcon from '../../../../reusable-component/LoadingIcon';
import { getXperienceGoldenRecordLineageApi } from "../../../../api's/DataProcessorApi";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

function GoldenRecordDetails({ goldenRecordId, handleToggleDetailPage, nodeId }) {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [recordDetails, setRecordDetails] = useState(null);

  const fieldColors = {
    // full_name: { borderColor: '#60a5fa', backgroundColor: '#eff6ff' },
    // email: { borderColor: '#4ade80', backgroundColor: '#f0fdf4' },
    // phone: { borderColor: '#a78bfa', backgroundColor: '#faf5ff' },
    // address: { borderColor: '#fb923c', backgroundColor: '#fff7ed' },
  };

  const fetchGoldenRecordLineage = async () => {
    setLoading(true);
    const data = {
      nodeId
    };
    try {
      const response = await getXperienceGoldenRecordLineageApi(goldenRecordId, data);
      if (response.status === 200) {
        setRecordDetails(response.data);
      }
    } catch (e) {
      // setRecordDetails(mockData);
      if (e?.response?.status === 404) {
        enqueueSnackbar('Lineage not found!', { variant: 'error' });
      } else {
        enqueueSnackbar('Something went wrong!', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGoldenRecordLineage();
  }, []);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Transform the data structure to match our UI needs
  const transformedData = useMemo(() => {
    if (!recordDetails) return null;

    const goldenRecord = recordDetails.lineage.source.golden_record_row;
    const fieldNames = Object.keys(goldenRecord);

    const sourceRecords = (recordDetails.lineage.records_used || []).map((record, index) => {
      const fields = {};

      record.lineage.forEach((lineageItem) => {
        fieldNames.forEach((fieldName) => {
          if (!fields[fieldName]) {
            fields[fieldName] = {
              value: record.row?.[fieldName] ?? 'N/A',
              selected: false,
              transformed: false,
              transformedValue: null,
              transformation: null,
            };
          }

          // Selected values
          if (Object.prototype.hasOwnProperty.call(lineageItem.selected_values || {}, fieldName)) {
            fields[fieldName].selected = true;
          }

          // Changed values (supports { from, to })
          if (Object.prototype.hasOwnProperty.call(lineageItem.changed_values || {}, fieldName)) {
            const change = lineageItem.changed_values[fieldName];
            fields[fieldName].transformed = true;
            fields[fieldName].value = typeof change === 'object' ? change.from : fields[fieldName].value;
            fields[fieldName].transformedValue = typeof change === 'object' ? change.to : change;

            fields[fieldName].transformation = {
              step: lineageItem.step,
              action: lineageItem.action,
              rules: lineageItem.details?.rules || [],
            };
          }
        });
      });

      return {
        id: record.record_id,
        label: `Record ${index + 1}`,
        fields,
      };
    });

    return {
      goldenRecord,
      sourceRecords,
      fieldNames,
    };
  }, [recordDetails]);

  if (!recordDetails || !transformedData)
    return (
      <>
        <Dialog fullScreen open TransitionComponent={Transition} style={{ width: '85%', marginLeft: '15%' }}>
          <AppBar sx={{ position: 'relative', backgroundColor: '#fff' }}>
            <Toolbar>
              <IconButton edge="start" onClick={handleToggleDetailPage} aria-label="close" sx={{ color: '#00043C' }}>
                <CloseIcon />
              </IconButton>
              <Typography sx={{ ml: 2, flex: 1, color: '#00043C' }} variant="h6" component="div">
                Golden Record Details
              </Typography>
            </Toolbar>
          </AppBar>

          {loading ? (
            <LoadingIcon />
          ) : (
            <Result
              status="404"
              title="404"
              subTitle="Lineage not found!"
            // extra={<Button type="primary">Back Home</Button>}
            />
          )}
        </Dialog>
      </>
    );

  const { goldenRecord, sourceRecords, fieldNames } = transformedData;

  return (
    <div>
      <Dialog fullScreen open TransitionComponent={Transition} style={{ width: '85%', marginLeft: '15%' }}>
        <AppBar sx={{ position: 'relative', backgroundColor: '#fff' }}>
          <Toolbar>
            <IconButton edge="start" onClick={handleToggleDetailPage} aria-label="close" sx={{ color: '#00043C' }}>
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1, color: '#00043C' }} variant="h6" component="div">
              Golden Record Details
            </Typography>
          </Toolbar>
        </AppBar>
        {loading ? (
          <LoadingIcon />
        ) : (
          <div style={{ padding: '24px', backgroundColor: '#fff' }}>
            <div style={{ marginBottom: '24px' }}>
              <p style={{ fontSize: '14px', color: '#4b5563', marginTop: '4px' }}>
                Golden Record ID: <b>{recordDetails.lineage.source.golden_record_id}</b>
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                {typeof recordDetails?.lineage?.source?.is_active === 'boolean' && (
                  <span
                    style={{
                      padding: '4px 12px',
                      backgroundColor: recordDetails.lineage.source.is_active ? '#d1fae5' : '#fee2e2',
                      color: recordDetails.lineage.source.is_active ? '#065f46' : '#991b1b',
                      borderRadius: '9999px',
                      fontSize: '12px',
                      fontWeight: '500',
                    }}
                  >
                    {recordDetails.lineage?.source?.is_active ? 'Active' : 'Inactive'}
                  </span>
                )}
                {typeof recordDetails?.lineage?.source?.confidence === 'number' && (
                  <span
                    style={{
                      padding: '4px 12px',
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      borderRadius: '9999px',
                      fontSize: '12px',
                      fontWeight: '500',
                    }}
                  >
                    Confidence: {Math.round(recordDetails.lineage.source.confidence * 100)}%
                  </span>
                )}
              </div>
            </div>

            {/* Golden Record Header */}
            <div
              style={{
                marginBottom: '24px',
                background: 'linear-gradient(to right, #e7e7e7ff, #f3f3f3ff)',
                borderRadius: '8px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                border: '2px solid #ddddddff',
                padding: '16px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <CheckCircle style={{ color: '#ca8a04' }} size={20} />
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
                  Golden Record (Master)
                </h2>
              </div>
              <div
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}
              >
                {Object.entries(goldenRecord).map(([field, value]) => {
                  const colorConfig = fieldColors[field] || { borderColor: '#e5e7eb', backgroundColor: '#f9fafb' };
                  return (
                    <div
                      key={field}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        border: `2px solid ${colorConfig.borderColor}`,
                        backgroundColor: colorConfig.backgroundColor,
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#4b5563',
                          textTransform: 'uppercase',
                          marginBottom: '4px',
                        }}
                      >
                        {field}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', wordBreak: 'break-word' }}>
                        {value || 'N/A'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Table */}
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                border: '1px solid #e5e7eb',
              }}
            >
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#374151',
                          textTransform: 'uppercase',
                          width: '48px',
                        }}
                      />
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#374151',
                          textTransform: 'uppercase',
                          minWidth: '150px',
                        }}
                      >
                        Record
                      </th>
                      {fieldNames.map((field) => (
                        <th
                          key={field}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: '#374151',
                            textTransform: 'uppercase',
                            minWidth: '180px',
                          }}
                        >
                          {field}
                        </th>
                      ))}
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#374151',
                          textTransform: 'uppercase',
                          minWidth: '120px',
                        }}
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceRecords.map((record, idx) => (
                      <React.Fragment key={record.id || idx}>
                        <tr
                          style={{
                            borderBottom: '1px solid #e5e7eb',
                            backgroundColor: expandedRows[record.id] ? '#eff6ff' : 'transparent',
                            transition: 'background-color 0.15s',
                          }}
                        >
                          <td style={{ padding: '12px 16px' }}>
                            <button
                              onClick={() => toggleRow(record.id)}
                              style={{
                                color: '#6b7280',
                                cursor: 'pointer',
                                border: 'none',
                                background: 'none',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              {expandedRows[record.id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                            </button>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>{record.label}</div>
                            <div
                              style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '150px',
                              }}
                            >
                              {record.id ? `${record.id.slice(0, 12)}...` : 'N/A'}
                            </div>
                          </td>
                          {fieldNames.map((field) => {
                            const fieldData = record.fields[field];
                            const isSelected = fieldData.selected;
                            const hasTransform = fieldData.transformed;
                            const colorConfig = fieldColors[field] || {
                              borderColor: '#e5e7eb',
                              backgroundColor: '#f9fafb',
                            };

                            return (
                              <td key={field} style={{ padding: '12px 16px' }}>
                                <div
                                  style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: isSelected ? `2px solid ${colorConfig.borderColor}` : '2px solid #e5e7eb',
                                    backgroundColor: isSelected ? colorConfig.backgroundColor : '#f9fafb',
                                    boxShadow: isSelected ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none',
                                    transition: 'all 0.15s',
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: '14px',
                                      color: '#1f2937',
                                      fontWeight: '500',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {fieldData.value}
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      marginTop: '4px',
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    {isSelected && (
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          padding: '2px 8px',
                                          backgroundColor: '#d1fae5',
                                          color: '#047857',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                        }}
                                      >
                                        <CheckCircle size={10} />
                                        Selected
                                      </span>
                                    )}
                                    {hasTransform && (
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          padding: '2px 8px',
                                          backgroundColor: '#dbeafe',
                                          color: '#1e40af',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                        }}
                                      >
                                        <Sparkles size={10} />
                                        Transform
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                          <td style={{ padding: '12px 16px' }}>
                            {Object.values(record.fields).some((v) => v.selected) ? (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '4px 12px',
                                  backgroundColor: '#d1fae5',
                                  color: '#047857',
                                  borderRadius: '9999px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <CheckCircle size={12} />
                                Contributed
                              </span>
                            ) : (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '4px 12px',
                                  backgroundColor: '#f3f4f6',
                                  color: '#4b5563',
                                  borderRadius: '9999px',
                                  fontSize: '12px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <AlertCircle size={12} />
                                Not Used
                              </span>
                            )}
                          </td>
                        </tr>

                        {/* Expanded Row - Transformation Details */}
                        {expandedRows[record.id] && (
                          <tr style={{ backgroundColor: '#eff6ff', borderBottom: '1px solid #bfdbfe' }}>
                            <td colSpan={fieldNames.length + 3} style={{ padding: '16px' }}>
                              <div style={{ marginLeft: '48px' }}>
                                <h4
                                  style={{
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    color: '#1f2937',
                                    marginBottom: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    margin: '0 0 12px 0',
                                  }}
                                >
                                  <Sparkles size={16} style={{ color: '#2563eb' }} />
                                  Transformation Details
                                </h4>
                                <div
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '16px',
                                  }}
                                >
                                  {fieldNames.map((field) => {
                                    const fieldData = record.fields[field];
                                    if (!fieldData.transformed) {
                                      return (
                                        <div
                                          key={field}
                                          style={{
                                            backgroundColor: 'white',
                                            borderRadius: '4px',
                                            border: '1px solid #e5e7eb',
                                            padding: '12px',
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: '12px',
                                              fontWeight: 'bold',
                                              color: '#4b5563',
                                              textTransform: 'uppercase',
                                              marginBottom: '8px',
                                            }}
                                          >
                                            {field}
                                          </div>
                                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                            No transformation applied
                                          </div>
                                        </div>
                                      );
                                    }

                                    return (
                                      <div
                                        key={field}
                                        style={{
                                          backgroundColor: 'white',
                                          borderRadius: '4px',
                                          border: '2px solid #93c5fd',
                                          padding: '12px',
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            color: '#4b5563',
                                            textTransform: 'uppercase',
                                            marginBottom: '8px',
                                          }}
                                        >
                                          {field}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                          {fieldData.transformation && (
                                            <>
                                              <div>
                                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Step</div>
                                                <div style={{ fontSize: '12px', fontWeight: '600', color: '#1d4ed8' }}>
                                                  {fieldData.transformation.step}
                                                </div>
                                              </div>
                                              <div>
                                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Action</div>
                                                <div style={{ fontSize: '12px', fontWeight: '600', color: '#1d4ed8' }}>
                                                  {fieldData.transformation.action}
                                                </div>
                                              </div>
                                              <div>
                                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Rules</div>
                                                <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                                                  {Array.isArray(fieldData.transformation.rules)
                                                    ? fieldData.transformation.rules.join(', ')
                                                    : fieldData.transformation.rules || 'N/A'}
                                                </div>
                                              </div>
                                            </>
                                          )}
                                          <div style={{ paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                              Value Change
                                            </div>
                                            <div
                                              style={{
                                                fontSize: '12px',
                                                color: '#4b5563',
                                                textDecoration: 'line-through',
                                              }}
                                            >
                                              {fieldData.value}
                                            </div>
                                            <div
                                              style={{
                                                fontSize: '12px',
                                                color: '#15803d',
                                                fontWeight: 'bold',
                                                marginTop: '4px',
                                              }}
                                            >
                                              → {fieldData.transformedValue}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Legend */}
            <div
              style={{
                marginTop: '24px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                border: '1px solid #e5e7eb',
                padding: '16px',
              }}
            >
              <h3
                style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937', marginBottom: '12px', marginTop: 0 }}
              >
                Legend
              </h3>
              <div
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}
              >
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563', marginBottom: '8px' }}>
                    Status Indicators
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CheckCircle size={14} style={{ color: '#059669' }} />
                      <span style={{ fontSize: '12px', color: '#374151' }}>Selected - Used in golden record</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Sparkles size={14} style={{ color: '#2563eb' }} />
                      <span style={{ fontSize: '12px', color: '#374151' }}>Transform - Data was transformed</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <AlertCircle size={14} style={{ color: '#4b5563' }} />
                      <span style={{ fontSize: '12px', color: '#374151' }}>
                        Not Used - Not selected for golden record
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}

export default GoldenRecordDetails;
